import React, { useRef, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { MdOutlineEmail } from 'react-icons/md'
import { ImWhatsapp } from 'react-icons/im'
import { IoCallOutline } from 'react-icons/io5'
import emailjs from '@emailjs/browser'
import './contact.css'
import { fadeInUp, hoverScale, staggerContainer, staggerItem } from '../../utils/animations'

// JSON Config
const CONFIG = {
  contact_options: [
    {
      id: 1,
      icon: 'MdOutlineEmail',
      title: 'Email Me',
      value: 'bothepiyush@gmail.com',
      link: 'mailto:bothepiyush@gmail.com',
      message: 'Send a Mail'
    },
    {
      id: 2,
      icon: 'ImWhatsapp',
      title: 'WhatsApp Me',
      value: '+91-7620673151',
      link: 'https://wa.me/+917620673151',
      message: 'Send a Message'
    },
    {
      id: 3,
      icon: 'IoCallOutline',
      title: 'Call Me',
      value: '+91-7620673151',
      link: 'tel:+917620673151',
      message: 'Call Now'
    }
  ],
  email_config: {
    service_id: 'service_2p456vo',
    template_id: 'template_sy4o3rd',
    public_key: 'LK2Sx_If5TGJPoy9b',
    validation_pattern: /^([\w.%+-]+)@(gmail\.com|outlook\.com|student\.ac\.in)$/
  }
}

// Mapping icons dynamically
const ICONS = {
  MdOutlineEmail: <MdOutlineEmail className="contact__icon" />,
  ImWhatsapp: <ImWhatsapp className="contact__icon" />,
  IoCallOutline: <IoCallOutline className="contact__icon" />
}

const Contact = () => {
  const form = useRef()
  const [formData, setFormData] = useState({ name: '', email: '', message: '' })
  const [errors, setErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState('')
  const [loading, setLoading] = useState(false)

  // Clear errors and success message after 5s
  useEffect(() => {
    if (Object.keys(errors).length || successMessage) {
      const timer = setTimeout(() => {
        setErrors({})
        setSuccessMessage('')
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [errors, successMessage])

  const validateForm = () => {
    const newErrors = {}
    if (formData.name.length < 3 || formData.name.length > 30) newErrors.name = 'Name should be 3-30 characters'
    if (!CONFIG.email_config.validation_pattern.test(formData.email)) newErrors.email = 'Enter a valid email (gmail, outlook, or student.ac.in)'
    if (formData.message.length < 10) newErrors.message = 'Message should be at least 10 characters'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const sendEmail = async e => {
    e.preventDefault()
    if (!validateForm()) return

    setLoading(true)
    try {
      const result = await emailjs.sendForm(
        CONFIG.email_config.service_id,
        CONFIG.email_config.template_id,
        form.current,
        CONFIG.email_config.public_key
      )
      if (result.status === 200) setSuccessMessage('Message sent successfully!')
      setFormData({ name: '', email: '', message: '' })
      form.current.reset()
    } catch (error) {
     // console.error('EmailJS error:', error)
      setErrors({ general: 'Error sending message. Please try again later.' })
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setErrors(prev => ({ ...prev, [name]: undefined, general: undefined }))
    setSuccessMessage('')
  }

  return (
    <motion.section
      id="contact"
      className="section contact-section"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
    >
      <motion.h5 className="section__subtitle" variants={fadeInUp}>Get In Touch</motion.h5>
      <motion.h2 className="section__title" variants={fadeInUp}>Contact Me</motion.h2>

      <motion.div
        className="container contact__container"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <motion.div className="contact__options" variants={staggerContainer}>
          {CONFIG.contact_options.map((option, index) => (
            <motion.article
              className="contact__option"
              key={option.id}
              variants={staggerItem}
              whileHover="hover"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
            >
              <h4>{ICONS[option.icon]} {option.title}</h4>
              <h5>{option.value}</h5>
              <motion.a
                href={option.link}
                target="_blank"
                rel="noreferrer"
                variants={hoverScale}
                whileHover="hover"
                whileTap="tap"
              >
                {option.message}
              </motion.a>
            </motion.article>
          ))}
        </motion.div>

        <motion.form
          ref={form}
          onSubmit={sendEmail}
          className="contact__form"
          variants={staggerItem}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.input
            type="text"
            name="name"
            placeholder="Your Full Name"
            value={formData.name}
            onChange={handleInputChange}
            required
            whileFocus={{ scale: 1.02, borderColor: '#4db5ff' }}
            transition={{ duration: 0.2 }}
          />
          <motion.input
            type="email"
            name="email"
            placeholder="Your Email"
            value={formData.email}
            onChange={handleInputChange}
            required
            whileFocus={{ scale: 1.02, borderColor: '#4db5ff' }}
            transition={{ duration: 0.2 }}
          />
          <motion.textarea
            name="message"
            rows="7"
            placeholder="Your Message"
            value={formData.message}
            onChange={handleInputChange}
            required
            whileFocus={{ scale: 1.02, borderColor: '#4db5ff' }}
            transition={{ duration: 0.2 }}
          />
          <motion.button type="submit" disabled={loading} variants={hoverScale} whileHover="hover" whileTap="tap">
            {loading ? 'Sending...' : 'Send Message'}
          </motion.button>

          {Object.keys(errors).length > 0 && (
            <div className="feedback-container">
              {Object.keys(errors).map(key => (
                <p key={key} className="error">{errors[key]}</p>
              ))}
            </div>
          )}
          {successMessage && <p className="success">{successMessage}</p>}
        </motion.form>
      </motion.div>
    </motion.section>
  )
}

export default Contact
